import { Layout, Menu, Avatar, Button, Space, message, ConfigProvider } from "antd";
import {
  HashRouter,
  Routes,
  Route,
  useNavigate as useReactNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import { adminLogout } from "./api"; 
import SettingBase from "./pages/admin/SettingBase";
import SettingSecurity from "./pages/admin/SettingSecurity";

import Dashboard from "./pages/Dashboard";
import UserList from "./pages/UserList";
import UserRole from "./pages/UserRole";
import HouseList from './pages/house/HouseList';
import RoomList from './pages/house/RoomList';
import BillList from './pages/house/BillList';
import ElectricMeterList from './pages/device/ElectricMeterList';
import WaterMeterList from './pages/device/WaterMeterList';
import LockList from './pages/device/LockList';
import OrderList from './pages/order/OrderList';
import SplitConfig from './pages/order/SplitConfig';
import HuifuAccountList from './pages/huifu/HuifuAccountList';
import HuifuBankCardList from './pages/huifu/HuifuBankCardList';
import Login from "./pages/login/Login";
import Unauthorized from "./pages/login/Unauthorized";
import ChangePassword from "./pages/admin/ChangePassword";
import SettingColumns from "./pages/admin/SettingColumns";
import "./index.css";
import { useState, useEffect, useCallback } from "react";
import {
  HomeOutlined,
  UserOutlined,
  ShopOutlined,
  SettingOutlined,
  ShoppingCartOutlined, // 订单管理图标
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ToolOutlined, // 设备管理图标
  BankOutlined, // 汇付管理图标
  AccountBookOutlined, // 分账管理图标
} from "@ant-design/icons";

// 管理员私有路由
const AdminPrivateRoute = ({ children }) => {
  const [adminToken, setAdminToken] = useState(localStorage.getItem("admin_token"));
  const location = useLocation();

  useEffect(() => {
    const handleStorageChange = () => {
      setAdminToken(localStorage.getItem("admin_token"));
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // 立即检查token，如果不存在则立即跳转
  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    const currentHash = window.location.hash;
    if (!token) {
      // token不存在，且不在未授权或登录页面，立即强制跳转
      if (currentHash !== '#/unauthorized' && currentHash !== '#/login') {
        window.location.replace('#/unauthorized');
      }
    }
  }, [location.pathname]);

  // 定期检查token是否存在（处理token被清除的情况）
  useEffect(() => {
    const checkToken = setInterval(() => {
      const token = localStorage.getItem("admin_token");
      const currentHash = window.location.hash;
      if (!token && adminToken) {
        // token被清除了，且不在未授权或登录页面，立即跳转
        if (currentHash !== '#/unauthorized' && currentHash !== '#/login') {
          window.location.replace('#/unauthorized');
        }
      }
    }, 300); // 每300毫秒检查一次，进一步提高响应速度

    return () => clearInterval(checkToken);
  }, [adminToken, location.pathname]);

  // 如果token不存在，直接返回null，不渲染任何内容，等待跳转
  if (!adminToken) {
    // 如果已经在未授权页面，则允许渲染
    if (location.pathname === '/unauthorized') {
      return children;
    }
    // 否则返回null，不渲染任何内容，强制跳转
    return null;
  }
  return children;
};

const { Header, Sider, Content } = Layout;

// antd v5 正确的菜单配置
const items = [
  { key: "/", label: "首页", icon: <HomeOutlined /> },
  {
    key: "/user", label: "用户管理", icon: <UserOutlined />,
    children: [{ key: "/user/list", label: "用户列表" }],
  },
  {
    key: "/house", label: "房屋管理", icon: <ShopOutlined />,
    children: [
      { key: "/house/house", label: "房源管理" },
      { key: "/house/room", label: "房间管理" },
      { key: "/house/bill", label: "账单管理" },
    ],
  },
  { 
    key: "/device", label: "设备管理", icon: <ToolOutlined />,
    children: [
      { key: "/device/electric", label: "电表管理" },
      { key: "/device/water", label: "水表管理" },
      { key: "/device/lock", label: "门锁管理" },
    ],
  },
  {
    key: "/order", label: "订单管理", icon: <ShoppingCartOutlined />,
    children: [
      { key: "/order/list", label: "订单列表" },
    ],
  },
  {
    key: "/split", label: "分账管理", icon: <AccountBookOutlined />,
    children: [
      { key: "/split/config", label: "分账配置" },
    ],
  },
  {
    key: "/huifu", label: "汇付管理", icon: <BankOutlined />,
    children: [
      { key: "/huifu/account", label: "汇付账号管理" },
      { key: "/huifu/bank-card", label: "汇付银行卡管理" },
    ],
  },
  {
    key: "/system", label: "系统设置", icon: <SettingOutlined />,
    children: [
      { key: "/system/change-password", label: "密码修改" },
      { key: "/system/base", label: "基础设置" },
      { key: "/system/security", label: "安全设置" },
      { key: "/system/columns", label: "列设置" },
    ],
  },
];

function AppLayout() {
  const navigate = useReactNavigate();
  const location = useLocation();
  const adminName = localStorage.getItem("admin_username") || "管理员";
  const [collapsed, setCollapsed] = useState(false);

  // 优化菜单点击处理，使用useCallback避免重复创建函数
  const handleMenuClick = useCallback(({ key }) => {
    navigate(key);
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await adminLogout().catch(err => console.log("后端退出接口失败，强制清除本地状态：", err));
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_username");
      message.success("退出登录成功");
      navigate("/login", { replace: true });
    } catch (err) {
      message.error("退出登录失败，请重试");
    }
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#3366FF',
          borderRadius: 6,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        },
        components: {
          Menu: {
            itemHeight: 32,
            subMenuItemBg: '#F8FAFC',
            itemSelectedBg: 'rgba(51, 102, 255, 0.1)',
            itemHoverBg: 'rgba(51, 102, 255, 0.08)',
            itemActiveBg: 'rgba(51, 102, 255, 0.12)',
            itemSelectedColor: '#3366FF',
            itemHoverColor: '#3366FF',
            fontSize: 11,
          },
          Table: {
            fontSize: 11,
            cellPaddingBlock: 4,
            cellPaddingInline: 8,
            headerBg: 'linear-gradient(135deg, rgba(51, 102, 255, 0.06) 0%, rgba(51, 102, 255, 0.03) 100%)',
            headerColor: '#1E293B',
            rowHoverBg: 'rgba(51, 102, 255, 0.05)',
            borderColor: 'rgba(51, 102, 255, 0.12)',
          },
          Button: {
            fontSize: 11,
            paddingContentHorizontal: 12,
            paddingContentVertical: 2,
            borderRadius: 5,
            controlHeight: 26,
          },
          Input: {
            fontSize: 11,
            paddingBlock: 2,
            paddingInline: 10,
            borderRadius: 5,
            controlHeight: 26,
          },
          Select: {
            fontSize: 11,
            borderRadius: 5,
            controlHeight: 26,
          },
        },
      }}
    >
      <Layout style={{ minHeight: "100vh", background: "transparent", position: 'relative', zIndex: 1 }}>
        <div style={{ position: 'relative' }}>
          <Sider 
            width={160} 
            collapsedWidth={56}
            collapsed={collapsed}
            theme="light" 
            style={{ 
              boxShadow: "4px 0 20px rgba(51, 102, 255, 0.1)",
              position: 'fixed',
              left: 0,
              top: 0,
              bottom: 0,
              zIndex: 100,
              borderRight: '1px solid rgba(51, 102, 255, 0.15)',
              background: '#ffffff',
              display: 'flex',
              flexDirection: 'column',
              height: '100vh',
              overflow: 'hidden',
            }}
          >
            <div style={{ 
              padding: collapsed ? "12px 12px" : "10px 16px", 
              fontWeight: 600, 
              fontSize: collapsed ? 13 : 14, 
              background: 'linear-gradient(135deg, #3366FF 0%, #5c85ff 100%)',
              color: "#fff",
              borderBottom: "1px solid rgba(255,255,255,0.15)",
              textAlign: collapsed ? 'center' : 'left',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(51, 102, 255, 0.25)',
              flexShrink: 0,
              letterSpacing: '0.3px',
            }}>
              {collapsed ? '管理' : '后台管理系统'}
            </div>
            <div 
              style={{ 
                flex: 1, 
                overflowY: 'auto', 
                overflowX: 'hidden',
                minHeight: 0, // 关键：允许flex子元素收缩
                WebkitOverflowScrolling: 'touch', // iOS平滑滚动
                maxHeight: 'calc(100vh - 50px)', // 确保不超过视口高度减去标题栏
              }}
              onWheel={(e) => {
                // 阻止滚轮事件冒泡到页面，确保只在导航栏内滚动
                e.stopPropagation();
              }}
            >
              <Menu
                mode="inline"
                selectedKeys={[location.pathname]}
                items={items}
                onClick={handleMenuClick}
                style={{ 
                  borderRight: 0, 
                  marginTop: 8, 
                  background: 'transparent',
                  minHeight: '100%', // 确保菜单至少占满容器
                }}
                inlineCollapsed={collapsed}
              />
            </div>
          </Sider>
          {/* 折叠按钮 - 竖长条样式 */}
          <div
            onClick={() => setCollapsed(!collapsed)}
            style={{
              position: 'fixed',
              left: collapsed ? 56 : 160,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 18,
              height: 50,
              background: 'linear-gradient(135deg, #3366FF 0%, #5c85ff 100%)',
              borderRadius: '0 8px 8px 0',
              cursor: 'pointer',
              zIndex: 101,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '2px 0 8px rgba(51, 102, 255, 0.3)',
              transition: 'all 0.2s',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderLeft: 'none',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '3px 0 10px rgba(51, 102, 255, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '2px 0 8px rgba(51, 102, 255, 0.3)';
            }}
          >
            <div style={{ 
              color: '#fff', 
              fontSize: 12,
              transition: 'all 0.2s',
            }}>
              {collapsed ? '▶' : '◀'}
            </div>
          </div>
        </div>
        <Layout style={{ marginLeft: collapsed ? 56 : 160, transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}>
          <Header style={{ 
            background: "#ffffff", 
            padding: "0 24px", 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center", 
            boxShadow: "0 2px 12px rgba(51, 102, 255, 0.08)", 
            zIndex: 10,
            height: 56,
            position: 'sticky',
            top: 0,
            borderBottom: '1px solid rgba(51, 102, 255, 0.12)',
          }}>
            <div style={{ 
              fontSize: 13, 
              color: "#1E293B", 
              fontWeight: 600,
              letterSpacing: '0.3px',
            }}>
              欢迎回来，<span style={{ color: '#3366FF', fontWeight: 600 }}>{adminName}</span>
            </div>
            <Space size="small">
              <Avatar style={{ 
                background: "linear-gradient(135deg, #3366FF 0%, #5c85ff 100%)", 
                fontSize: 12, 
                cursor: 'pointer', 
                boxShadow: '0 2px 6px rgba(51, 102, 255, 0.3)', 
                width: 32, 
                height: 32, 
                lineHeight: '32px',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 3px 8px rgba(51, 102, 255, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(51, 102, 255, 0.3)';
              }}
              >{adminName.charAt(0).toUpperCase()}</Avatar>
              <Button 
                type="primary" 
                danger 
                size="small" 
                onClick={handleLogout} 
                style={{ 
                  padding: '0 14px', 
                  fontSize: 11, 
                  height: 28,
                  fontWeight: 600,
                }}
              >
                退出登录
              </Button>
            </Space>
          </Header>
          <Content style={{ 
            margin: 20, 
            padding: 20, 
            background: "#ffffff", 
            borderRadius: 12, 
            minHeight: 280, 
            boxShadow: '0 4px 20px rgba(51, 102, 255, 0.08)', 
            border: '1px solid rgba(51, 102, 255, 0.12)',
          }}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/user/list" element={<UserList />} />
              <Route path="/user/role/:id" element={<UserRole />} />
              <Route path="/house/house" element={<HouseList />} />
              <Route path="/house/room" element={<RoomList />} />
              <Route path="/house/bill" element={<BillList />} />
              <Route path="/device/electric" element={<ElectricMeterList />} />
              <Route path="/device/water" element={<WaterMeterList />} />
              <Route path="/device/lock" element={<LockList />} />
              <Route path="/order/list" element={<OrderList />} />
              <Route path="/split/config" element={<SplitConfig />} />
              <Route path="/huifu/account" element={<HuifuAccountList />} />
              <Route path="/huifu/bank-card" element={<HuifuBankCardList />} />
              <Route path="/system/change-password" element={<ChangePassword />} />
              <Route path="/system/base" element={<SettingBase />} />
              <Route path="/system/security" element={<SettingSecurity />} />
              <Route path="/system/columns" element={<SettingColumns />} />
              <Route path="/*" element={<Navigate to="/" />} />
            </Routes>
          </Content>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
}

export default function App() {
  return (
    <HashRouter> 
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/*" element={<AdminPrivateRoute><AppLayout /></AdminPrivateRoute>} />
      </Routes>
    </HashRouter>
  );
}